import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight, FiArrowLeft, FiLoader } = FiIcons;

const TypeformStep = ({
  children,
  currentStep,
  totalSteps,
  progressPercentage,
  onNext,
  onPrev,
  canProceed = true,
  isLastStep = false,
  nextButtonText = "Continue",
  isSubmitting = false,
  isQuestionStep = false
}) => {
  // Use progressPercentage if provided, otherwise calculate from currentStep
  const progress = progressPercentage !== undefined ? progressPercentage : (currentStep / totalSteps) * 100;
  
  // Check if this is the welcome page (first step)
  const isWelcomePage = currentStep === 1;

  return (
    <div className="min-h-screen bg-dark-700 flex flex-col" style={{ backgroundColor: '#121212' }}>
      {/* Enhanced Progress Bar with Percentage */}
      <div className="w-full bg-dark-600 relative">
        <div className="max-w-7xl mx-auto px-4 py-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-gray-400">Progress</span>
            <span className="text-xs font-bold text-primary-500">{Math.round(progress)}%</span>
          </div>
          <div className="w-full bg-dark-400 h-3 rounded-full overflow-hidden shadow-inner">
            <motion.div
              className="h-full bg-gradient-to-r from-primary-600 to-primary-500 rounded-full relative"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              style={{
                boxShadow: progress > 0 ? '0 0 10px rgba(217, 4, 41, 0.5)' : 'none'
              }}
            >
              {/* Shimmer effect */}
              {progress > 0 && progress < 100 && (
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30"
                  animate={{
                    x: ['-100%', '200%']
                  }}
                  transition={{
                    repeat: Infinity,
                    duration: 2,
                    ease: "linear"
                  }}
                  style={{ width: '50%' }}
                />
              )}
            </motion.div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 flex items-center justify-center px-4 py-8 md:py-12">
        <div className="w-full max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="space-y-8"
          >
            {children}
          </motion.div>
        </div>
      </div>

      {/* Navigation */}
      <div className="p-4 md:p-6 flex justify-center items-center max-w-4xl mx-auto w-full">
        {isWelcomePage ? (
          // For welcome page, center a larger button
          <div className="flex justify-center w-full">
            {canProceed && (
              <button
                onClick={onNext}
                disabled={!canProceed || isSubmitting}
                className={`inline-flex items-center px-8 py-4 sm:px-12 sm:py-5 md:px-16 md:py-6 rounded-lg text-lg sm:text-xl font-semibold transition-all duration-300 ${
                  canProceed 
                    ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-lg hover:shadow-xl' 
                    : 'bg-dark-300 text-gray-500 cursor-not-allowed'
                }`}
                style={{ 
                  background: canProceed ? 'linear-gradient(135deg, #d90429 0%, #ef233c 100%)' : '',
                  transform: 'translateY(0)'
                }}
                onMouseOver={(e) => {
                  if (canProceed) e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseOut={(e) => {
                  if (canProceed) e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                {isSubmitting && (
                  <SafeIcon icon={FiLoader} className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 animate-spin" />
                )}
                <span className="whitespace-normal text-center">{nextButtonText}</span>
              </button>
            )}
          </div>
        ) : (
          // For all other steps (questions and contact form), use the standard layout
          <div className="flex items-center justify-between w-full max-w-xl">
            {/* Fixed-width container for back button */}
            <div className="w-24 text-left">
              {(currentStep > 1 || isLastStep) && (
                <button
                  onClick={onPrev}
                  disabled={isSubmitting}
                  className={`inline-flex items-center px-3 py-2 md:px-4 md:py-2 text-gray-300 hover:text-white transition-colors ${
                    isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <SafeIcon icon={FiArrowLeft} className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Back</span>
                </button>
              )}
            </div>

            {/* Fixed center for page number */}
            <div className="text-sm text-gray-400 w-24 text-center">
              {!isLastStep && currentStep > 1 && `${currentStep} of ${totalSteps}`}
            </div>

            {/* Fixed-width container for next button */}
            <div className="w-24 text-right">
              {canProceed && (
                <button
                  onClick={onNext}
                  disabled={!canProceed || isSubmitting}
                  className={`inline-flex items-center justify-center px-4 py-2 sm:px-6 sm:py-3 md:px-10 md:py-4 rounded-lg font-semibold transition-all duration-300 ${
                    canProceed 
                      ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-lg hover:shadow-xl' 
                      : 'bg-dark-300 text-gray-500 cursor-not-allowed'
                  }`}
                  style={{ 
                    background: canProceed ? 'linear-gradient(135deg, #d90429 0%, #ef233c 100%)' : '',
                    transform: 'translateY(0)'
                  }}
                  onMouseOver={(e) => {
                    if (canProceed) e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseOut={(e) => {
                    if (canProceed) e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  {isSubmitting && (
                    <SafeIcon icon={FiLoader} className="w-4 h-4 mr-1 sm:mr-2 animate-spin" />
                  )}
                  <span className="text-xs sm:text-sm md:text-base whitespace-nowrap">{nextButtonText}</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TypeformStep;